import React, { useState } from 'react';
import styled from 'styled-components';
import { FileText, X } from 'lucide-react';

const LogsButton = styled.button`
  background: var(--accent-secondary);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px auto;
  box-shadow: var(--shadow-md);
  transition: all 0.2s ease;

  &:hover {
    background: var(--accent-secondary-hover);
  }
`;

const LogsModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LogsContainer = styled.div`
  background: var(--bg-primary);
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  height: 80%;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
`;

const LogsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-primary);
  border-radius: 12px 12px 0 0;
  transition: all 0.3s ease;
`;

const LogsTitle = styled.h2`
  margin: 0;
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  transition: color 0.3s ease;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
`;

const LogsContent = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  background: var(--bg-secondary);
  color: var(--text-primary);
  white-space: pre-wrap;
  transition: all 0.3s ease;
`;

interface LogsButtonProps {
  logs: Array<{
    id: string;
    timestamp: string;
    level: 'info' | 'success' | 'error' | 'warning';
    message: string;
    strategy?: string;
    tx_hash?: string;
    explorer_url?: string;
  }>;
}

const ShowLogsButton: React.FC<LogsButtonProps> = ({ logs }) => {
  const [showLogs, setShowLogs] = useState(false);

  return (
    <>
      <LogsButton onClick={() => setShowLogs(true)}>
        <FileText size={16} />
        🖥️ DCA Terminal ({logs.length})
      </LogsButton>

      {showLogs && (
        <LogsModal onClick={() => setShowLogs(false)}>
          <LogsContainer onClick={(e) => e.stopPropagation()}>
            <LogsHeader>
              <LogsTitle>DCA Terminal Logs</LogsTitle>
              <CloseButton onClick={() => setShowLogs(false)}>
                <X size={20} />
              </CloseButton>
            </LogsHeader>
            
            <LogsContent>
              {logs.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px' }}>
                  No DCA logs available
                </div>
              ) : (
                logs.slice().reverse().map((log) => {
                  // Форматируем время как в терминале
                  const timestamp = new Date(log.timestamp).toISOString().replace('T', ' ').replace('Z', '');
                  
                  // Определяем цвет по уровню лога
                  let color = '#e5e5e5'; // info
                  if (log.level === 'error') {
                    color = '#ff6b6b';
                  } else if (log.level === 'success') {
                    color = '#51cf66';
                  } else if (log.level === 'warning') {
                    color = '#ffd43b';
                  }
                  
                  return (
                    <div key={log.id} style={{ marginBottom: '2px', color }}>
                      <span style={{ color: '#6b7280' }}>{timestamp}</span>
                      <span style={{ color: '#8b5cf6', marginLeft: '8px' }}>|</span>
                      <span style={{ color: '#6b7280', marginLeft: '8px' }}>INFO</span>
                      <span style={{ color: '#8b5cf6', marginLeft: '8px' }}>|</span>
                      <span style={{ color: '#8b5cf6', marginLeft: '8px' }}>dca_eth_bot</span>
                      <span style={{ color: '#8b5cf6', marginLeft: '8px' }}>|</span>
                      <span style={{ color, marginLeft: '8px' }}>{log.message}</span>
                      {log.explorer_url && (
                        <div style={{ marginTop: '4px', marginLeft: '20px' }}>
                          <a 
                            href={log.explorer_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                              color: '#3b82f6', 
                              textDecoration: 'none', 
                              fontSize: '0.75rem' 
                            }}
                          >
                            🔗 {log.explorer_url}
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </LogsContent>
          </LogsContainer>
        </LogsModal>
      )}
    </>
  );
};

export default ShowLogsButton;
